import type { ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

/**
 * A page's title and its action bar (docs/11 §2). At most three buttons, in the
 * order "···", secondary, primary — the primary always rightmost. On a phone the
 * bar takes the full width and the primary moves first, across the whole row;
 * the rest share the row below, an icon button keeping its square. The space
 * under the header belongs to the page that places it.
 */
export function PageHeader({
    title,
    description,
    meta,
    actions,
    breadcrumb,
    className,
}: {
    title: ReactNode;
    description?: ReactNode;
    /** The record's state under its title: status and plan badges, say. */
    meta?: ReactNode;
    actions?: ReactNode;
    /** The way back out of a detail page, above its title. */
    breadcrumb?: ReactNode;
    className?: string;
}) {
    return (
        <header data-slot="page-header" className={cn('flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between', className)}>
            <div className="min-w-0">
                {breadcrumb && <div className="mb-2">{breadcrumb}</div>}
                <h1 className="text-28 font-semibold tracking-title">{title}</h1>
                {description && <p className="mt-1 text-16 text-muted-foreground">{description}</p>}
                {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
            </div>
            {actions && (
                <div
                    data-slot="page-actions"
                    className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:*:not-data-[size=icon]:flex-auto max-sm:*:data-[variant=primary]:order-first max-sm:*:data-[variant=primary]:basis-full"
                >
                    {actions}
                </div>
            )}
        </header>
    );
}
